import {
  collection,
  doc,
  getDocs,
  getDocsFromServer,
  getDoc,
  setDoc,
  deleteDoc,
  onSnapshot,
  Timestamp,
  serverTimestamp,
  query,
  orderBy,
  writeBatch,
  type DocumentData,
  type Unsubscribe,
  type SnapshotListenOptions,
} from 'firebase/firestore';
import { Platform } from 'react-native';
import { db } from '@/lib/firebase';
import { networkMonitor } from './networkMonitor';
import { syncQueue } from './syncQueue';
import { getCachedTripData, getCachedTrip, cacheTripData } from './offlineTrips';
import { isFirestoreClientOfflineLikeError, runWithFirestoreReconnectGrace } from '@/utils/firestoreConnectivity';
import { recordFirestoreRead } from '@/utils/firestoreTelemetry';
import { coerceDate, fromLocalDateKey, toLocalDateKey } from '@/utils/dateKeys';
import type { Trip } from '@/services/trips';
import { getItemTripCalendarDateKey } from '@/utils/tripCalendar';
import { toDateKeyInTimezone } from '@/utils/tripItemSchedule';
import { shouldPreserveReservationAnchoredItineraryDates } from '@/utils/itineraryReservationAnchors';
import { mergeItineraryTimeOntoDay } from '@/utils/mergeItineraryTimeOntoDay';
import { geocodeAddress } from './maps';
import {
  buildPointGeofence,
  startGeofenceMonitoring,
  type GeofenceEvent,
  type Geofence,
} from './geofencing';
import { sendItineraryCheckInNotification } from './notifications';
import {
  type ItineraryAttachment,
  normalizeAttachments,
} from './itineraryAttachments';

const NATIVE_ITINERARY_POLL_MS = 30000;

/**
 * Firestore does not allow `undefined` anywhere in the payload (including inside arrays/objects).
 * Normalize itinerary attachments for safe persistence.
 */
function serializeAttachmentsForFirestore(attachments: ItineraryAttachment[]): any[] {
  return (attachments || []).map((att) => {
    const out: Record<string, any> = {
      id: att.id,
      name: att.name,
      url: att.url,
    };

    if (att.contentType !== undefined) out.contentType = att.contentType;
    if (att.size !== undefined) out.size = att.size;
    if (att.source !== undefined) out.source = att.source;
    if (att.offlinePath !== undefined) out.offlinePath = att.offlinePath;

    if (att.createdAt !== undefined) {
      out.createdAt = att.createdAt instanceof Date ? Timestamp.fromDate(att.createdAt) : att.createdAt;
    }
    if (att.expiresAt !== undefined) {
      out.expiresAt = att.expiresAt instanceof Date ? Timestamp.fromDate(att.expiresAt) : att.expiresAt;
    }
    if (att.confidence !== undefined) out.confidence = att.confidence;

    return out;
  });
}

/**
 * Firestore does not allow `undefined` anywhere (including nested objects).
 * Shallow-sanitize an object by removing undefined fields, preserving nulls.
 */
function stripUndefinedShallow<T extends Record<string, any>>(obj: T): Partial<T> {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (v === undefined) continue;
    out[k] = v;
  }
  return out as Partial<T>;
}

export type ItineraryItemCategory =
  | 'flight'
  | 'hotel'
  | 'activity'
  | 'restaurant'
  | 'car'
  | 'shopping'
  | 'transportation'
  | 'train'
  | 'ferry'
  | 'other';
export type ItineraryItemSource = 'manual' | 'email' | 'ai' | 'template';
export type ItineraryItemStatus = 'planned' | 'confirmed' | 'completed' | 'cancelled';

export type ItineraryItemNotes = {
  text: string;
  isShared: boolean;
  updatedAt: Date;
  updatedBy: string;
};

export type ItineraryBookingSnapshot = {
  type?: string | null;
  provider?: string | null;
  status?: string | null;
  confirmationNumber?: string | null;
  flightNumber?: string | null;
  airlineCode?: string | null;
  gate?: string | null;
  terminal?: string | null;
  checkInLink?: string | null;
  amount?: number | null;
  currency?: string | null;
  location?: string | null;
  startDate?: Date | null;
  rawEmailSubject?: string | null;
  rawEmailDate?: string | null;
};

export type ItineraryItem = {
  id: string;
  tripId: string;
  title: string;
  description?: string;
  category: ItineraryItemCategory;
  startDate: Date;
  endDate?: Date;
  startTime?: string; // Time string (HH:mm)
  endTime?: string;
  /**
   * Optional index into the parent trip's `destinations[]` for multi-city trips.
   * Used to disambiguate overlap/travel days where multiple destinations share a calendar date.
   */
  cityIndex?: number | null;
  address?: string;
  location?: string;
  /** Map pin / geocode cache: persisted when the map resolves an address. */
  coordinates?: { latitude: number; longitude: number } | null;
  airlineCode?: string;
  flightNumber?: string;
  bookingId?: string; // Reference to booking if auto-mapped
  bookingSnapshot?: ItineraryBookingSnapshot | null;
  source: ItineraryItemSource;
  documents: string[]; // Array of Firebase Storage URLs
  attachments?: ItineraryAttachment[];
  notes?: ItineraryItemNotes;
  status: ItineraryItemStatus;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
};

type FirestoreItineraryItem = {
  title: string;
  description?: string;
  category: string;
  startDate: Timestamp | string;
  endDate?: Timestamp | string;
  startTime?: string;
  endTime?: string;
  cityIndex?: number | null;
  address?: string;
  location?: string;
  coordinates?: { latitude: number; longitude: number } | null;
  airlineCode?: string;
  flightNumber?: string;
  bookingId?: string;
  bookingSnapshot?: {
    [key: string]: any;
  };
  source: string;
  documents?: string[];
  attachments?: Array<Partial<ItineraryAttachment>>;
  notes?: {
    text?: string | null;
    isShared?: boolean | null;
    updatedAt?: Timestamp | Date | string | null;
    updatedBy?: string | null;
  } | null;
  status: string;
  createdBy: string;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
};

// Convert Firestore itinerary item to app ItineraryItem type
const convertFirestoreItineraryItem = (id: string, data: FirestoreItineraryItem): ItineraryItem => {
  const convertDate = (date: Timestamp | string | Date | undefined): Date | undefined => {
    if (!date) return undefined;
    if (date instanceof Timestamp) {
      try {
        const d = date.toDate();
        // IMPORTANT: Sometimes date-only values are accidentally stored as a Timestamp created from
        // `new Date('YYYY-MM-DD')` which is interpreted as midnight UTC. When converted to a local
        // Date, that can shift to the previous day and break day-based grouping (multi-city travel days).
        //
        // If it's exactly midnight in UTC, treat it as a date-only and re-parse as a LOCAL calendar date.
        if (
          d.getUTCHours() === 0 &&
          d.getUTCMinutes() === 0 &&
          d.getUTCSeconds() === 0 &&
          d.getUTCMilliseconds() === 0
        ) {
          const y = d.getUTCFullYear();
          const m = String(d.getUTCMonth() + 1).padStart(2, '0');
          const day = String(d.getUTCDate()).padStart(2, '0');
          return fromLocalDateKey(`${y}-${m}-${day}`);
        }
        return d;
      } catch (error) {
        console.error('[Itinerary] Error converting Timestamp to Date:', error);
        return undefined;
      }
    }
    if (date instanceof Date) {
      // Validate the date is not invalid
      if (isNaN(date.getTime())) {
        console.error('[Itinerary] Invalid Date object:', date);
        return undefined;
      }
      return date;
    }
    if (typeof date === 'string') {
      // IMPORTANT: parse YYYY-MM-DD as a local date, not UTC. `new Date('YYYY-MM-DD')`
      // is treated as UTC and can shift the day in non-UTC timezones.
      // ALSO handle ISO strings that are effectively "date-only" at midnight UTC (e.g. 2025-05-13T00:00:00.000Z),
      // which can shift to the previous day in local timezones.
      const dateOnlyMatch = /^(\d{4}-\d{2}-\d{2})$/.exec(date);
      const isoMidnightUtcMatch = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.000)?Z$/.exec(date);
      const parsed = dateOnlyMatch
        ? fromLocalDateKey(dateOnlyMatch[1])
        : isoMidnightUtcMatch
          ? fromLocalDateKey(isoMidnightUtcMatch[1])
          : new Date(date);
      if (isNaN(parsed.getTime())) {
        console.error('[Itinerary] Invalid date string:', date);
        return undefined;
      }
      return parsed;
    }
    // Handle other types that might have a toDate method (like Firestore Timestamp from different versions)
    if (date && typeof date === 'object' && 'toDate' in date && typeof (date as any).toDate === 'function') {
      try {
        return (date as any).toDate();
      } catch (error) {
        console.error('[Itinerary] Error calling toDate() on date object:', error);
        return undefined;
      }
    }
    console.warn('[Itinerary] Unknown date type:', typeof date, date);
    return undefined;
  };

  // Convert startDate with validation - it's required, so we need a fallback
  let startDate = convertDate(data.startDate);
  if (!startDate || isNaN(startDate.getTime())) {
    console.error('[Itinerary] Invalid startDate for item:', id, data.startDate);
    // Use a default date (today) as last resort, but log the error
    startDate = new Date();
  }

  return {
    id,
    tripId: '', // Will be set by caller
    title: data.title,
    description: data.description,
    category: data.category as ItineraryItemCategory,
    startDate,
    endDate: convertDate(data.endDate),
    startTime: data.startTime,
    endTime: data.endTime,
    cityIndex: typeof (data as any).cityIndex === 'number' ? (data as any).cityIndex : (data as any).cityIndex ?? null,
    address: data.address,
    location: data.location,
    coordinates: data.coordinates ?? null,
    airlineCode: data.airlineCode || undefined,
    flightNumber: data.flightNumber || undefined,
    bookingId: data.bookingId,
    bookingSnapshot: data.bookingSnapshot
      ? {
          ...data.bookingSnapshot,
          startDate: data.bookingSnapshot.startDate instanceof Timestamp
            ? data.bookingSnapshot.startDate.toDate()
            : data.bookingSnapshot.startDate || undefined,
        }
      : undefined,
    source: data.source as ItineraryItemSource,
    attachments: normalizeAttachments(
      data.attachments as Array<Partial<ItineraryAttachment>> | undefined,
      data.documents || []
    ),
    documents: (data.attachments && data.attachments.length > 0)
      ? normalizeAttachments(
          data.attachments as Array<Partial<ItineraryAttachment>>,
          data.documents || []
        ).map(att => att.url)
      : data.documents || [],
    notes: (() => {
      const n = (data as any).notes as any;
      if (!n || typeof n !== 'object') return undefined;
      const text = typeof n.text === 'string' ? n.text : '';
      const isShared = Boolean(n.isShared);
      const updatedAt = convertDate(n.updatedAt) || undefined;
      const updatedBy = typeof n.updatedBy === 'string' ? n.updatedBy : '';
      if (!text.trim()) return undefined;
      if (!updatedAt || !updatedBy) {
        // If partial data exists, still surface it with safe fallbacks.
        return {
          text,
          isShared,
          updatedAt: updatedAt || new Date(),
          updatedBy: updatedBy || 'unknown',
        };
      }
      return { text, isShared, updatedAt, updatedBy };
    })(),
    status: data.status as ItineraryItemStatus,
    createdBy: data.createdBy,
    createdAt: convertDate(data.createdAt) || new Date(),
    updatedAt: convertDate(data.updatedAt) || new Date(),
  };
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const utcDayNumber = (date: Date): number =>
  Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / MS_PER_DAY;

const dayDelta = (from: Date, to: Date): number => utcDayNumber(to) - utcDayNumber(from);

const addDaysPreserveTime = (date: Date, deltaDays: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + deltaDays);
  return next;
};

/**
 * Calendar-normalized reads for Firestore itinerary dates used by shift/realign helpers.
 * MUST stay aligned with {@link convertFirestoreItineraryItem} timestamp/string handling —
 * raw `Timestamp.toDate()` treats date-only values stored at UTC midnight as the prior/next local day,
 * which made repairs report "already aligned" while Plan still flagged rows as outside the trip window.
 */
const convertFirestoreDate = (raw: any): Date | null => {
  if (!raw) return null;
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
  const normalizeUtcMidnightCalendarDay = (d: Date): Date => {
    if (
      d.getUTCHours() === 0 &&
      d.getUTCMinutes() === 0 &&
      d.getUTCSeconds() === 0 &&
      d.getUTCMilliseconds() === 0
    ) {
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, '0');
      const day = String(d.getUTCDate()).padStart(2, '0');
      return fromLocalDateKey(`${y}-${m}-${day}`);
    }
    return d;
  };
  if (raw instanceof Timestamp) {
    return normalizeUtcMidnightCalendarDay(raw.toDate());
  }
  if (typeof raw === 'string' || typeof raw === 'number') {
    const s = String(raw);
    const dateOnlyMatch = /^(\d{4}-\d{2}-\d{2})$/.exec(s);
    const isoMidnightUtcMatch = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.000)?Z$/.exec(s);
    const parsed = dateOnlyMatch
      ? fromLocalDateKey(dateOnlyMatch[1])
      : isoMidnightUtcMatch
        ? fromLocalDateKey(isoMidnightUtcMatch[1])
        : new Date(s);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  if (raw && typeof raw === 'object' && typeof raw.toDate === 'function') {
    try {
      const d = raw.toDate();
      if (!(d instanceof Date) || isNaN(d.getTime())) return null;
      return normalizeUtcMidnightCalendarDay(d);
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Fetch all itinerary items for a trip.
 * Pass `{ fromServer: true }` after repairs or explicit resync — `getDocs` alone can satisfy
 * from the SDK local cache until the listener reconciles with the server (Plan looked "stuck").
 */
export const fetchTripItineraryItems = async (
  tripId: string,
  options?: { fromServer?: boolean },
): Promise<ItineraryItem[]> => {
  try {
    const itemsRef = collection(db, 'trips', tripId, 'itineraryItems');
    const q = query(itemsRef, orderBy('startDate', 'asc'));
    const preferServer = options?.fromServer === true;
    let snapshot;

    let readSource: 'getDocs' | 'getDocsFromServer' = 'getDocs';
    if (preferServer && networkMonitor.isOnline()) {
      try {
        snapshot = await getDocsFromServer(q);
        readSource = 'getDocsFromServer';
      } catch {
        snapshot = await getDocs(q);
        readSource = 'getDocs';
      }
    } else {
      snapshot = await getDocs(q);
      readSource = 'getDocs';
    }

    recordFirestoreRead({
      source: readSource,
      collectionPath: `trips/${tripId}/itineraryItems`,
      docCount: snapshot.docs.length,
      fromCache: snapshot.metadata?.fromCache,
      status: 'success',
      metadata: {
        hasPendingWrites: snapshot.metadata?.hasPendingWrites,
      },
    });

    return snapshot.docs.map((doc) => {
      const item = convertFirestoreItineraryItem(doc.id, doc.data() as FirestoreItineraryItem);
      item.tripId = tripId;
      return item;
    });
  } catch (error) {
    console.error('Error fetching itinerary items:', error);
    return [];
  }
};

/**
 * Rewrite the offline trip pack's itinerary slice from Firestore so cold starts
 * don't re-hydrate pre-repair dates from AsyncStorage (Plan hydrates cache before snapshot).
 */
async function refreshOfflineItineraryPackFromServer(tripId: string): Promise<void> {
  const cached = await getCachedTripData(tripId);
  if (!cached?.trip) return;
  const items = await fetchTripItineraryItems(tripId, {
    fromServer: networkMonitor.isOnline(),
  });
  await cacheTripData(
    cached.trip,
    {
      expenses: cached.expenses,
      bookings: cached.bookings,
      itinerary: items,
    },
    { force: true },
  );
}

async function removeItineraryItemFromOfflineCache(tripId: string, itemId: string): Promise<void> {
  const cached = await getCachedTripData(tripId);
  if (!cached?.trip || !Array.isArray(cached.itinerary)) return;
  const nextItinerary = cached.itinerary.filter((row) => {
    const id = typeof row === 'object' && row != null && 'id' in row ? String((row as { id?: unknown }).id) : '';
    return id !== itemId;
  });
  if (nextItinerary.length === cached.itinerary.length) return;
  await cacheTripData(
    cached.trip,
    {
      expenses: cached.expenses,
      bookings: cached.bookings,
      itinerary: nextItinerary,
    },
    { force: true },
  );
}

/**
 * Shift itinerary items by a day offset when a user changes the trip start date.
 *
 * We shift movable plan content so Day 1 maps to the new Day 1, but we preserve rows that anchor to
 * real reservations (`source === 'email'`, partner bookings like travel_provider/amadeus). Planner
 * entries linked to manual/smart_upload/AI/template bookings still move — see `@/utils/itineraryReservationAnchors`.
 */
export const shiftTripTemplateItineraryItemDates = async (
  tripId: string,
  options: {
    oldStartDate: Date;
    newStartDate: Date;
    /**
     * When true, only shift rows with source === 'template'.
     * Use for the "fix template dates" repair so manual/AI items before the trip
     * do not skew the anchor day used to realign template content.
     */
    onlyShiftTemplateSource?: boolean;
  },
): Promise<{ updated: number; scanned: number; deltaDays: number }> => {
  const deltaDays = dayDelta(options.oldStartDate, options.newStartDate);
  if (deltaDays === 0) {
    return { updated: 0, scanned: 0, deltaDays };
  }

  const itemsRef = collection(db, 'trips', tripId, 'itineraryItems');
  const snapshot = await getDocs(itemsRef);

  let updated = 0;
  let scanned = 0;
  const onlyTemplate = options.onlyShiftTemplateSource === true;

  // Firestore batched writes are limited to 500 operations per batch.
  const MAX_OPS = 450;
  let batch = writeBatch(db);
  let ops = 0;

  const flush = async () => {
    if (ops === 0) return;
    await batch.commit();
    batch = writeBatch(db);
    ops = 0;
  };

  for (const docSnap of snapshot.docs) {
    scanned += 1;
    const data = docSnap.data() as Record<string, any>;
    // Skip inbox/provider-backed rows only; movable planner drafts may still carry bookingId links.
    if (shouldPreserveReservationAnchoredItineraryDates(data)) {
      continue;
    }
    if (onlyTemplate && data?.source !== 'template') {
      continue;
    }

    const start = convertFirestoreDate(data.startDate);
    if (!start) {
      continue;
    }

    const nextStart = addDaysPreserveTime(start, deltaDays);

    const updatePayload: Record<string, any> = {
      startDate: Timestamp.fromDate(nextStart),
      updatedAt: serverTimestamp(),
    };

    // Only update endDate if the field exists on the doc (avoid creating it).
    if (Object.prototype.hasOwnProperty.call(data, 'endDate')) {
      const end = convertFirestoreDate(data.endDate);
      updatePayload.endDate = end ? Timestamp.fromDate(addDaysPreserveTime(end, deltaDays)) : null;
    }

    batch.set(docSnap.ref, updatePayload, { merge: true });
    ops += 1;
    updated += 1;

    if (ops >= MAX_OPS) {
      await flush();
    }
  }

  await flush();

  if (updated > 0) {
    try {
      await refreshOfflineItineraryPackFromServer(tripId);
    } catch (error) {
      console.warn('[Itinerary] Failed to refresh offline pack after date shift', {
        tripId,
        error,
      });
    }
  }

  return { updated, scanned, deltaDays };
};

/**
 * Fallback repair: align the earliest **template** itinerary day to the trip's start date.
 * This helps when the trip's oldStartDate is missing/incorrect or when items were created
 * with a different anchor date than the trip record.
 *
 * Important: we anchor on template rows only. Using the earliest manual/AI item would skew
 * the day offset and leave template activities outside the trip window (or report 0 updates).
 */
export const realignTemplateItineraryToTripStart = async (
  tripId: string,
  tripStartDate: Date,
): Promise<{ updated: number; scanned: number; deltaDays: number }> => {
  let shiftWrotePack = false;
  try {
    const itemsRef = collection(db, 'trips', tripId, 'itineraryItems');
    const snapshot = await getDocs(itemsRef);

    let scanned = 0;
    let earliestTemplateStart: Date | null = null;

    for (const docSnap of snapshot.docs) {
      scanned += 1;
      const data = docSnap.data() as Record<string, any>;
      if (shouldPreserveReservationAnchoredItineraryDates(data)) continue;
      if (data?.source !== 'template') continue;
      const start = convertFirestoreDate(data.startDate);
      if (!start) continue;
      if (!earliestTemplateStart || start.getTime() < earliestTemplateStart.getTime()) {
        earliestTemplateStart = start;
      }
    }

    if (!earliestTemplateStart) {
      return { updated: 0, scanned, deltaDays: 0 };
    }

    // Normalize both to local calendar-day midnight before computing delta.
    const oldAnchor = fromLocalDateKey(toLocalDateKey(earliestTemplateStart));
    const newAnchor = fromLocalDateKey(toLocalDateKey(tripStartDate));
    const deltaDays = dayDelta(oldAnchor, newAnchor);

    if (deltaDays === 0) {
      return { updated: 0, scanned, deltaDays };
    }

    // Re-run the same shift logic using the computed anchor.
    const result = await shiftTripTemplateItineraryItemDates(tripId, {
      oldStartDate: oldAnchor,
      newStartDate: newAnchor,
      onlyShiftTemplateSource: true,
    });

    shiftWrotePack = result.updated > 0;
    return { ...result, scanned };
  } finally {
    // shiftTripTemplateItineraryItemDates already refreshes the pack when it updates rows; skip the
    // duplicate full fetch+write (was doubling latency on "Fix itinerary dates").
    if (!shiftWrotePack) {
      // Do not block the UI on this best-effort refresh; iOS may pause network work
      // when app state changes, which previously left the "Fixing..." button spinning.
      void refreshOfflineItineraryPackFromServer(tripId).catch((error) => {
        console.warn('[Itinerary] Failed to refresh offline pack after template realign', {
          tripId,
          error,
        });
      });
    }
  }
};

/**
 * Subscribe to real-time updates for trip itinerary items
 */
export const subscribeToTripItineraryItems = (
  tripId: string,
  callback: (items: ItineraryItem[]) => void
): (() => void) => {
  if (Platform.OS !== 'web') {
    let isCleanedUp = false;
    const poll = async () => {
      try {
        const items = await fetchTripItineraryItems(tripId);
        if (!isCleanedUp) {
          callback(items);
        }
      } catch (error) {
        console.error('[Itinerary] Native polling fetch failed:', error);
        if (!isCleanedUp) {
          callback([]);
        }
      }
    };
    void poll();
    const interval = setInterval(() => {
      void poll();
    }, NATIVE_ITINERARY_POLL_MS);
    return () => {
      isCleanedUp = true;
      clearInterval(interval);
    };
  }

  const itemsRef = collection(db, 'trips', tripId, 'itineraryItems');
  const collectionPath = `trips/${tripId}/itineraryItems`;
  
  // Try with orderBy first, but if it fails, fall back to unordered query
  let unsubscribeFn: (() => void) | null = null;
  let isCleanedUp = false;
  let didRetryPermission = false;
  
  const emitErrorTelemetry = (errorCode?: string, extras: Record<string, unknown> = {}) => {
    recordFirestoreRead({
      source: 'onSnapshot',
      collectionPath,
      docCount: 0,
      status: 'error',
      metadata: {
        errorCode,
        ...extras,
      },
    });
  };

  const itinerarySnapshotOptions: SnapshotListenOptions = {
    includeMetadataChanges: true,
  };

  const setupSubscription = (useOrderBy: boolean = true): Unsubscribe | null => {
    // Don't set up subscription if already cleaned up
    if (isCleanedUp) {
      return null;
    }
    
    try {
      const q = useOrderBy 
        ? query(itemsRef, orderBy('startDate', 'asc'))
        : query(itemsRef);
      
      return onSnapshot(q, itinerarySnapshotOptions, (snapshot) => {
        // Don't process updates if already cleaned up
        if (isCleanedUp) {
          return;
        }
        
        try {
          // Log cache status for debugging
          if (__DEV__) {
            console.log('[Itinerary] Subscription update', {
              count: snapshot.docs.length,
              fromCache: snapshot.metadata.fromCache,
            });
          }
          
          // Convert documents to items (optimized - no intermediate logging in production)
            const items = snapshot.docs.map(doc => {
              try {
              const item = convertFirestoreItineraryItem(doc.id, doc.data() as FirestoreItineraryItem);
                item.tripId = tripId;
                return item;
              } catch (error) {
              console.error(`[Itinerary] Error converting item ${doc.id}:`, error);
                return null;
              }
            }).filter((item): item is ItineraryItem => item !== null);
            
          // Sort manually to ensure correct order (only if not already ordered by query)
          if (useOrderBy) {
            // Items should already be sorted by Firestore, but validate
            const needsSort = items.some((item, i) => 
              i > 0 && item.startDate.getTime() < items[i - 1].startDate.getTime()
            );
            if (needsSort) {
              items.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
            }
          } else {
            // No orderBy - must sort manually
            items.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
          }
          
          // Record telemetry
            recordFirestoreRead({
              source: 'onSnapshot',
              collectionPath,
              docCount: items.length,
              fromCache: snapshot.metadata.fromCache,
              status: 'success',
              metadata: {
                hasPendingWrites: snapshot.metadata.hasPendingWrites,
                useOrderBy,
              },
            });
          
          // Callback with results
            callback(items);
          } catch (error) {
            console.error('[Itinerary] Error processing snapshot:', error);
            if (!isCleanedUp) {
              try {
                callback([]);
              } catch (callbackError) {
                console.error('[Itinerary] Error in callback:', callbackError);
              }
            }
          }
      }, (error): void => {
        // Don't process errors if already cleaned up
        if (isCleanedUp) {
          return;
        }
        
        const errCode = String((error as { code?: string })?.code ?? '');
        // For permission errors, try to repair membership once, then resubscribe.
        if (errCode === 'permission-denied' || errCode === 'missing-or-insufficient-permissions') {
          emitErrorTelemetry(error?.code, {
            useOrderBy,
            membershipRepairAttempted: didRetryPermission,
          });
          if (!didRetryPermission) {
            didRetryPermission = true;
            console.log('[Itinerary] Permission error, attempting membership repair');
            void (async () => {
              try {
                const { syncTripMembership } = await import('@/services/tripMembership');
                const result = await syncTripMembership(tripId);
                console.log('[Itinerary] Membership repair result', { success: result?.success });
                
                if (!result?.success) {
                  console.warn('[Itinerary] Membership repair returned success=false');
                  if (!isCleanedUp) {
                    try {
                      callback([]);
                    } catch {
                      // ignore
                    }
                  }
                  return;
                }
              } catch (syncError) {
                console.warn('[Itinerary] Membership sync failed after permission error', syncError);
                if (!isCleanedUp) {
                  try {
                    callback([]);
                  } catch {
                    // ignore
                  }
                }
                return;
              }
              
              if (isCleanedUp) return;
              
              // Small delay before resubscribing to allow Firestore rules to propagate
              await new Promise(resolve => setTimeout(resolve, 500));
              
              if (isCleanedUp) return;
              
              if (unsubscribeFn) {
                try {
                  unsubscribeFn();
                } catch {
                  // ignore
                }
                unsubscribeFn = null;
              }
              if (!isCleanedUp) {
                console.log('[Itinerary] Resubscribing after membership repair');
                try {
                  unsubscribeFn = setupSubscription(useOrderBy);
                } catch (setupError) {
                  console.error('[Itinerary] Error re-subscribing after permission repair', setupError);
                }
              }
            })();
          } else {
            // If permission is still denied after one repair attempt, always unblock downstream
            // consumers so tab-level loading indicators do not spin indefinitely.
            console.warn('[Itinerary] Permission still denied after membership repair; emitting empty snapshot');
            if (!isCleanedUp) {
              try {
                callback([]);
              } catch {
                // ignore
              }
            }
          }
          return;
        }
        
        console.error('[Itinerary] Error in subscription:', error);
        emitErrorTelemetry(error?.code, { useOrderBy });
        if (isFirestoreClientOfflineLikeError(error)) {
          if (__DEV__) {
            console.warn('[Itinerary] Subscription transient/connectivity error — omitting empty callback');
          }
          return;
        }
        
        // If it's an index error, try without ordering
        if (error?.code === 'failed-precondition' && useOrderBy && !isCleanedUp) {
          console.warn('[Itinerary] Index missing, retrying without orderBy');
          const oldUnsubscribe = unsubscribeFn;
          unsubscribeFn = null; // Clear before setting up new subscription
          if (oldUnsubscribe) {
            try {
              oldUnsubscribe(); // Unsubscribe from the failed query
            } catch (unsubError) {
              // Silently ignore - subscription may already be invalid
            }
          }
          if (!isCleanedUp) {
            try {
              unsubscribeFn = setupSubscription(false);
            } catch (setupError) {
              console.error('[Itinerary] Error setting up fallback subscription:', setupError);
            }
          }
          return;
        }
        
        // For other errors, call callback with empty array
        try {
          callback([]);
        } catch (callbackError) {
          console.error('[Itinerary] Error in callback:', callbackError);
        }
      });
    } catch (error) {
      console.error('[Itinerary] Error setting up subscription:', error);
      return null;
    }
  };
  
  try {
    unsubscribeFn = setupSubscription(true);
  } catch (error) {
    console.error('[Itinerary] Fatal error setting up subscription:', error);
    unsubscribeFn = null;
  }
  
  return () => {
    if (isCleanedUp) {
      return; // Already cleaned up, prevent double cleanup
    }
    isCleanedUp = true;
    
    if (unsubscribeFn) {
      try {
        unsubscribeFn();
      } catch (error) {
        // Silently ignore cleanup errors - they're often due to Firestore internal state
      } finally {
        unsubscribeFn = null; // Clear reference after cleanup
      }
    }
  };
};

/**
 * Get a single itinerary item
 */
export const getItineraryItem = async (tripId: string, itemId: string): Promise<ItineraryItem | null> => {
  try {
    const itemRef = doc(db, 'trips', tripId, 'itineraryItems', itemId);
    const snapshot = await getDoc(itemRef);
    
    if (!snapshot.exists()) {
      return null;
    }
    
    const item = convertFirestoreItineraryItem(snapshot.id, snapshot.data() as FirestoreItineraryItem);
    item.tripId = tripId;
    return item;
  } catch (error) {
    console.error('Error fetching itinerary item:', error);
    return null;
  }
};

/** When true, `createItineraryItem` runs the Firestore path (used by sync queue flush). */
let itineraryCreateFromSyncFlush = false;

function itineraryCreatePayloadForQueue(
  item: Omit<ItineraryItem, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>,
): Record<string, unknown> {
  return {
    ...item,
    startDate: item.startDate.toISOString(),
    endDate: item.endDate ? item.endDate.toISOString() : undefined,
    bookingSnapshot: item.bookingSnapshot
      ? {
          ...item.bookingSnapshot,
          startDate: item.bookingSnapshot.startDate
            ? item.bookingSnapshot.startDate.toISOString()
            : null,
        }
      : undefined,
    notes: item.notes
      ? {
          ...item.notes,
          updatedAt: (item.notes.updatedAt || new Date()).toISOString(),
        }
      : undefined,
    attachments: item.attachments?.map((a) => ({
      ...a,
      createdAt: a.createdAt ? a.createdAt.toISOString() : undefined,
    })),
  };
}

function itineraryItemFromQueuePayload(
  raw: Record<string, unknown>,
): Omit<ItineraryItem, 'id' | 'tripId' | 'createdAt' | 'updatedAt'> {
  const o = raw as any;
  return {
    ...o,
    startDate: new Date(o.startDate),
    endDate: o.endDate ? new Date(o.endDate) : undefined,
    bookingSnapshot: o.bookingSnapshot
      ? {
          ...o.bookingSnapshot,
          startDate: o.bookingSnapshot.startDate ? new Date(o.bookingSnapshot.startDate) : null,
        }
      : undefined,
    notes: o.notes
      ? {
          ...o.notes,
          updatedAt: o.notes.updatedAt ? new Date(o.notes.updatedAt) : new Date(),
        }
      : undefined,
    attachments: Array.isArray(o.attachments)
      ? o.attachments.map((a: any) => ({
          ...a,
          createdAt: a.createdAt ? new Date(a.createdAt) : undefined,
        }))
      : o.attachments,
  };
}

async function queueCreateItineraryItemOffline(
  tripId: string,
  item: Omit<ItineraryItem, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const cached = await getCachedTripData(tripId);
  const cachedTrip = cached?.trip ?? (await getCachedTrip(tripId));

  const offlineId = `offline_itin_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  const now = new Date();
  const itineraryRow = {
    ...item,
    id: offlineId,
    tripId,
    createdAt: now,
    updatedAt: now,
  };

  if (cachedTrip) {
    const prev = Array.isArray(cached?.itinerary) ? [...cached.itinerary] : [];
    prev.push(itineraryRow);
    await cacheTripData(
      cachedTrip,
      {
        expenses: cached?.expenses,
        bookings: cached?.bookings,
        itinerary: prev,
      },
      { force: true },
    );
  }

  await syncQueue.queueAction({
    type: 'create_itinerary_item',
    payload: {
      tripId,
      offlineId,
      item: itineraryCreatePayloadForQueue(item),
    },
    tripId,
    userId: item.createdBy,
  });

  return offlineId;
}

const GET_TRIP_DOC_FOR_ITINERARY_TIMEOUT_MS = 12_000;

/**
 * Load trip doc for validation; if the app thinks it's online but Firestore never answers
 * (captive portal / flaky link), fall back to the offline itinerary queue when a trip pack exists.
 */
async function getTripSnapshotForItineraryCreate(
  tripId: string,
  tripRef: ReturnType<typeof doc>,
): Promise<Awaited<ReturnType<typeof getDoc>> | 'use_offline_queue'> {
  const fetchWithMembershipRepair = async (): Promise<Awaited<ReturnType<typeof getDoc>>> => {
    try {
      return await getDoc(tripRef);
    } catch (err: any) {
      const { isPermissionError, syncTripMembership } = await import('@/services/tripMembership');
      if (isPermissionError(err)) {
        const res = await syncTripMembership(tripId);
        if (res?.success) {
          return await getDoc(tripRef);
        }
      }
      throw err;
    }
  };

  const snapPromise = fetchWithMembershipRepair();
  const timedOut = new Promise<'timeout'>((resolve) =>
    setTimeout(() => resolve('timeout'), GET_TRIP_DOC_FOR_ITINERARY_TIMEOUT_MS),
  );
  const raced = await Promise.race([
    snapPromise.then((snap) => ({ tag: 'snap' as const, snap })),
    timedOut.then(() => ({ tag: 'timeout' as const })),
  ]);

  if (raced.tag === 'snap') {
    return raced.snap;
  }

  const cached = await getCachedTripData(tripId);
  const hasOfflineTripContext = Boolean(cached?.trip || (await getCachedTrip(tripId)));
  if (hasOfflineTripContext && !itineraryCreateFromSyncFlush) {
    void snapPromise.catch(() => {});
    return 'use_offline_queue';
  }

  return await snapPromise;
}

/**
 * Create a new itinerary item
 */
export const createItineraryItem = async (
  tripId: string,
  item: Omit<ItineraryItem, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  try {
    if (!networkMonitor.isOnline() && !itineraryCreateFromSyncFlush) {
      return await queueCreateItineraryItemOffline(tripId, item);
    }

    // Fetch trip to validate dates
    const tripRef = doc(db, 'trips', tripId);
    const tripDocOrOffline = await getTripSnapshotForItineraryCreate(tripId, tripRef);
    if (tripDocOrOffline === 'use_offline_queue') {
      return await queueCreateItineraryItemOffline(tripId, item);
    }
    const tripDoc = tripDocOrOffline;

    if (!tripDoc.exists()) {
      throw new Error('Trip not found');
    }
    
    const tripData = tripDoc.data() as DocumentData;
    const coerceTripDate = (value: any): Date | null => {
      if (!value) return null;
      try {
        const d = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
        return d instanceof Date && !isNaN(d.getTime()) ? d : null;
      } catch {
        return null;
      }
    };

    const tripStartRaw = coerceTripDate(tripData.startDate);
    const tripEndRaw = coerceTripDate(tripData.endDate);

    // Some trips have a 1-day `startDate/endDate` but multi-city `destinations[]` spans multiple days.
    // Validate itinerary items against the effective window that includes destinations.
    const destinationDates: Date[] = [];
    if (Array.isArray(tripData.destinations)) {
      for (const dest of tripData.destinations) {
        const ds = coerceTripDate(dest?.startDate);
        const de = coerceTripDate(dest?.endDate);
        if (ds) destinationDates.push(ds);
        if (de) destinationDates.push(de);
      }
    }

    const allTripDates = [tripStartRaw, tripEndRaw, ...destinationDates].filter(
      (d): d is Date => d instanceof Date && !isNaN(d.getTime()),
    );

    // Fallback: if trip dates are missing/invalid, anchor to the item date so we don't throw meaningless range errors.
    const tripStartDate =
      allTripDates.length > 0
        ? new Date(Math.min(...allTripDates.map((d) => d.getTime())))
        : new Date(item.startDate);
    const tripEndDate =
      allTripDates.length > 0
        ? new Date(Math.max(...allTripDates.map((d) => d.getTime())))
        : new Date(item.startDate);

    // Hint context for optional background address enrichment (geocoding). Do not block create on this —
    // sequential Mapbox calls made saves feel stuck on device ("Saving..." for many seconds).
    const tripDestinationHint =
      tripData.destination ||
      (Array.isArray(tripData.destinations) && tripData.destinations.length > 0
        ? tripData.destinations[0].city || tripData.destinations[0].name
        : null) ||
      null;
    const tripDestinationForEnrichment =
      typeof tripDestinationHint === 'string' ? tripDestinationHint : undefined;
    const isDepartureFlightHint =
      item.category === 'flight' &&
      (item.title.toLowerCase().includes('departure') ||
        item.title.toLowerCase().includes('depart'));
    const isArrivalFlightHint =
      item.category === 'flight' &&
      (item.title.toLowerCase().includes('arrival') ||
        item.title.toLowerCase().includes('arrive'));

    // Normalize dates to compare only date portion (ignore time)
    const normalizeDate = (date: Date): Date => {
      const normalized = new Date(date);
      normalized.setHours(0, 0, 0, 0);
      return normalized;
    };
    
    const normalizedStartDate = normalizeDate(item.startDate);
    const normalizedTripStart = normalizeDate(tripStartDate);
    const normalizedTripEnd = normalizeDate(tripEndDate);
    const tripWindowDays = Math.round((normalizedTripEnd.getTime() - normalizedTripStart.getTime()) / (1000 * 60 * 60 * 24));
    const isSingleDayTripWindow = tripWindowDays < 1 && destinationDates.length === 0;
    
    // Travel categories (flights/trains/transport) can legitimately fall outside the trip window.
    // Do not block persistence; the UI can decide how/where to display them.
    const isTransportCategory = ['flight', 'train', 'transportation', 'car', 'ferry'].includes(item.category);
    if (!isTransportCategory && !isSingleDayTripWindow) {
      // Validate start date is within trip range
      if (normalizedStartDate < normalizedTripStart || normalizedStartDate > normalizedTripEnd) {
        throw new Error(
          `Start date must be between ${tripStartDate.toLocaleDateString()} and ${tripEndDate.toLocaleDateString()}`
        );
      }
    }
    
    // Validate end date if provided
    if (item.endDate) {
      const normalizedEndDate = normalizeDate(item.endDate);
      if (!isTransportCategory && !isSingleDayTripWindow) {
        if (normalizedEndDate < normalizedTripStart || normalizedEndDate > normalizedTripEnd) {
          throw new Error(
            `End date must be between ${tripStartDate.toLocaleDateString()} and ${tripEndDate.toLocaleDateString()}`
          );
        }
      }
      if (normalizedEndDate < normalizedStartDate) {
        throw new Error('End date must be after start date');
      }
    }
    
    const itemsRef = collection(db, 'trips', tripId, 'itineraryItems');
    const newItemRef = doc(itemsRef);
    
    const normalizedAttachments = normalizeAttachments(
      item.attachments,
      item.documents
    );

    // Build firestore item, converting undefined to null or omitting the field
    const firestoreItem: Record<string, any> = {
      title: item.title,
      category: item.category,
      startDate: Timestamp.fromDate(item.startDate),
      source: item.source,
      attachments: serializeAttachmentsForFirestore(normalizedAttachments),
      documents: (item.documents && item.documents.length > 0)
        ? item.documents
        : normalizedAttachments.map(att => att.url),
      status: item.status,
      createdBy: item.createdBy,
    };
    
    // Only add optional fields if they have values (not undefined)
    if (item.description !== undefined) {
      firestoreItem.description = item.description || null;
    }
    if (item.endDate !== undefined) {
      firestoreItem.endDate = item.endDate ? Timestamp.fromDate(item.endDate) : null;
    }
    if (item.startTime !== undefined) {
      firestoreItem.startTime = item.startTime || null;
    }
    if (item.endTime !== undefined) {
      firestoreItem.endTime = item.endTime || null;
    }
    if (item.cityIndex !== undefined) {
      firestoreItem.cityIndex = item.cityIndex ?? null;
    }
    if (item.address !== undefined) {
      firestoreItem.address = item.address || null;
    }
    if (item.location !== undefined) {
      firestoreItem.location = item.location || null;
    }
  if (item.airlineCode !== undefined) {
    firestoreItem.airlineCode = item.airlineCode || null;
  }
  if (item.flightNumber !== undefined) {
    firestoreItem.flightNumber = item.flightNumber || null;
  }
    if (item.bookingId !== undefined) {
      firestoreItem.bookingId = item.bookingId || null;
    }
  if (item.bookingSnapshot !== undefined) {
    const snapshot = item.bookingSnapshot;
    firestoreItem.bookingSnapshot = snapshot
      ? (() => {
          const sanitized = stripUndefinedShallow(snapshot as any) as Record<string, any>;
          // Normalize nested date field
          if ('startDate' in sanitized) {
            sanitized.startDate = sanitized.startDate ? Timestamp.fromDate(sanitized.startDate) : null;
          }
          return sanitized;
        })()
      : null;
  }

    if ((item as any).notes !== undefined) {
      const notes = (item as any).notes as ItineraryItemNotes | undefined;
      firestoreItem.notes = notes
        ? {
            text: notes.text || '',
            isShared: Boolean(notes.isShared),
            updatedAt: Timestamp.fromDate(notes.updatedAt || new Date()),
            updatedBy: notes.updatedBy || item.createdBy,
          }
        : null;
    }
    
    try {
      await setDoc(newItemRef, {
        ...firestoreItem,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (err: any) {
      // Legacy trips can have collaborators without members/memberIds. Repair and retry once.
      const { isPermissionError, syncTripMembership } = await import('@/services/tripMembership');
      if (isPermissionError(err)) {
        const res = await syncTripMembership(tripId);
        if (!res?.success) {
          throw err;
        }
        await setDoc(newItemRef, {
          ...firestoreItem,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } else {
        throw err;
      }
    }

    const newItemId = newItemRef.id;
    const shouldEnrichAddressLater =
      networkMonitor.isOnline() && (!item.address || !item.address.trim());

    if (shouldEnrichAddressLater) {
      void (async () => {
        try {
          const { enrichItineraryItemAddress } = await import('./addressEnrichment');
          const enriched = await enrichItineraryItemAddress(item, {
            tripDestination: tripDestinationForEnrichment,
            isDeparture: isDepartureFlightHint,
            isArrival: isArrivalFlightHint,
          });
          const patch: Partial<
            Omit<ItineraryItem, 'id' | 'tripId' | 'createdBy' | 'createdAt' | 'updatedAt'>
          > = {};
          const nextAddr = enriched.address?.trim();
          const prevAddr = item.address?.trim();
          if (nextAddr && nextAddr !== prevAddr) {
            patch.address = enriched.address;
          }
          const nextLoc = enriched.location?.trim();
          const prevLoc = item.location?.trim();
          if (nextLoc && nextLoc !== prevLoc) {
            patch.location = enriched.location;
          }
          if (Object.keys(patch).length === 0) {
            return;
          }
          await updateItineraryItem(tripId, newItemId, patch, { bypassOffline: true });
        } catch (e) {
          console.warn('[Itinerary] Background address enrichment failed:', e);
        }
      })();
    }

    return newItemId;
  } catch (error) {
    console.error('Error creating itinerary item:', error);
    throw error;
  }
};

/**
 * Update an existing itinerary item
 */
export const updateItineraryItem = async (
  tripId: string,
  itemId: string,
  updates: Partial<Omit<ItineraryItem, 'id' | 'tripId' | 'createdBy' | 'createdAt' | 'updatedAt'>>,
  options?: { bypassOffline?: boolean }
): Promise<void> => {
  if (!options?.bypassOffline && networkMonitor.shouldUseOfflineDataFallback()) {
    await syncQueue.queueAction({
      type: 'update_itinerary_item',
      payload: {
        tripId,
        itemId,
        updatesJson: JSON.stringify(updates, (_k, v) => (v instanceof Date ? v.toISOString() : v)),
      },
      tripId,
    });
    return;
  }

  try {
    const itemRef = doc(db, 'trips', tripId, 'itineraryItems', itemId);
    let currentItemDoc = await getDoc(itemRef);
    if (!currentItemDoc.exists()) {
      throw new Error('Itinerary item not found');
    }
    let currentItemData = currentItemDoc.data();

    // Fetch trip to validate dates if they're being updated
    if (updates.startDate !== undefined || updates.endDate !== undefined) {
      const tripRef = doc(db, 'trips', tripId);
      const tripDoc = await getDoc(tripRef);
      
      if (!tripDoc.exists()) {
        throw new Error('Trip not found');
      }
      
      const tripData = tripDoc.data();
      const tripStartDate = tripData.startDate?.toDate?.() || new Date(tripData.startDate);
      const tripEndDate = tripData.endDate?.toDate?.() || new Date(tripData.endDate);
      
      // Normalize dates to compare only date portion (ignore time)
      const normalizeDate = (date: Date): Date => {
        const normalized = new Date(date);
        normalized.setHours(0, 0, 0, 0);
        return normalized;
      };
      
      const normalizedTripStart = normalizeDate(tripStartDate);
      const normalizedTripEnd = normalizeDate(tripEndDate);
      
      // Get current item to validate end date against start date
      const currentCategoryRaw = (updates.category ?? currentItemData.category) as string | undefined;
      const currentCategory = typeof currentCategoryRaw === 'string' ? currentCategoryRaw.toLowerCase() : '';
      const isTransportCategory = ['flight', 'train', 'transportation', 'car', 'ferry'].includes(currentCategory);
      const currentStartDate = updates.startDate 
        ? updates.startDate 
        : (currentItemData.startDate?.toDate?.() || new Date(currentItemData.startDate));
      
      // Validate start date if being updated
      if (updates.startDate !== undefined) {
        const normalizedStartDate = normalizeDate(updates.startDate);
        if (!isTransportCategory) {
          if (normalizedStartDate < normalizedTripStart || normalizedStartDate > normalizedTripEnd) {
            throw new Error(
              `Start date must be between ${tripStartDate.toLocaleDateString()} and ${tripEndDate.toLocaleDateString()}`
            );
          }
        }
      }
      
      // Validate end date if being updated
      if (updates.endDate !== undefined) {
        if (updates.endDate) {
          const normalizedEndDate = normalizeDate(updates.endDate);
          if (!isTransportCategory) {
            if (normalizedEndDate < normalizedTripStart || normalizedEndDate > normalizedTripEnd) {
              throw new Error(
                `End date must be between ${tripStartDate.toLocaleDateString()} and ${tripEndDate.toLocaleDateString()}`
              );
            }
          }
          const normalizedStartDate = normalizeDate(currentStartDate);
          if (normalizedEndDate < normalizedStartDate) {
            throw new Error('End date must be after start date');
          }
        }
      }
    }

    const firestoreUpdates: Record<string, any> = {};
    
    // Only add fields that are explicitly provided, converting undefined/empty strings to null
    if (updates.title !== undefined) firestoreUpdates.title = updates.title;
    if (updates.description !== undefined) firestoreUpdates.description = updates.description || null;
    if (updates.category !== undefined) firestoreUpdates.category = updates.category;
    if (updates.startDate !== undefined) firestoreUpdates.startDate = Timestamp.fromDate(updates.startDate);
    if (updates.endDate !== undefined) firestoreUpdates.endDate = updates.endDate ? Timestamp.fromDate(updates.endDate) : null;
    if (updates.startTime !== undefined) firestoreUpdates.startTime = updates.startTime || null;
    if (updates.endTime !== undefined) firestoreUpdates.endTime = updates.endTime || null;
    if (updates.cityIndex !== undefined) firestoreUpdates.cityIndex = updates.cityIndex ?? null;
    if (updates.address !== undefined) firestoreUpdates.address = updates.address || null;
    if (updates.location !== undefined) firestoreUpdates.location = updates.location || null;
    if (updates.coordinates !== undefined) {
      firestoreUpdates.coordinates =
        updates.coordinates &&
        Number.isFinite(updates.coordinates.latitude) &&
        Number.isFinite(updates.coordinates.longitude)
          ? {
              latitude: updates.coordinates.latitude,
              longitude: updates.coordinates.longitude,
            }
          : null;
    }
    if (updates.airlineCode !== undefined) firestoreUpdates.airlineCode = updates.airlineCode || null;
    if (updates.flightNumber !== undefined) firestoreUpdates.flightNumber = updates.flightNumber || null;
    if (updates.bookingId !== undefined) firestoreUpdates.bookingId = updates.bookingId || null;
  if (updates.bookingSnapshot !== undefined) {
    firestoreUpdates.bookingSnapshot = updates.bookingSnapshot
      ? {
          ...updates.bookingSnapshot,
          startDate: updates.bookingSnapshot.startDate
            ? Timestamp.fromDate(updates.bookingSnapshot.startDate)
            : null,
        }
      : null;
  }
    if (updates.source !== undefined) firestoreUpdates.source = updates.source;
    if (updates.documents !== undefined) firestoreUpdates.documents = updates.documents || [];
    if (updates.attachments !== undefined) {
      const normalized = normalizeAttachments(
        updates.attachments,
        updates.documents
      );
      firestoreUpdates.attachments = serializeAttachmentsForFirestore(normalized);
      if (updates.documents === undefined) {
        // Keep legacy `documents` field in sync (url-only).
        firestoreUpdates.documents = normalized.map(att => att.url);
      }
    }
    if (updates.status !== undefined) firestoreUpdates.status = updates.status;
    if ((updates as any).notes !== undefined) {
      const notes = (updates as any).notes as ItineraryItemNotes | undefined;
      firestoreUpdates.notes = notes
        ? {
            text: notes.text || '',
            isShared: Boolean(notes.isShared),
            updatedAt: Timestamp.fromDate(notes.updatedAt || new Date()),
            updatedBy: notes.updatedBy || 'unknown',
          }
        : null;
    }
    
    await setDoc(itemRef, {
      ...firestoreUpdates,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    // Keep linked booking flight identifiers in sync so status refresh uses edited values.
    const currentCategory = typeof currentItemData.category === 'string'
      ? currentItemData.category.toLowerCase()
      : '';
    const effectiveCategory = (updates.category ?? currentCategory) as string;
    const linkedBookingId = (updates.bookingId !== undefined ? updates.bookingId : currentItemData.bookingId) as string | undefined;
    if (effectiveCategory === 'flight' && linkedBookingId) {
      const toNullableCode = (value: unknown) => {
        if (typeof value !== 'string') return null;
        const normalized = value.trim().toUpperCase();
        return normalized.length > 0 ? normalized : null;
      };
      const toOptionalText = (value: unknown): string | undefined => {
        if (typeof value !== 'string') return undefined;
        const normalized = value.trim();
        return normalized.length > 0 ? normalized : undefined;
      };
      const toNullableDate = (value: unknown): Date | null => {
        if (!value) return null;
        if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
        if (typeof (value as any)?.toDate === 'function') {
          const converted = (value as any).toDate();
          return converted instanceof Date && !Number.isNaN(converted.getTime()) ? converted : null;
        }
        const parsed = new Date(value as any);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      };

      const previousAirlineCode = toNullableCode(currentItemData.airlineCode);
      const previousFlightNumber = toNullableCode(currentItemData.flightNumber);
      const nextAirlineCode = toNullableCode(
        updates.airlineCode !== undefined ? updates.airlineCode : currentItemData.airlineCode,
      );
      const nextFlightNumber = toNullableCode(
        updates.flightNumber !== undefined ? updates.flightNumber : currentItemData.flightNumber,
      );
      const identifiersChanged =
        previousAirlineCode !== nextAirlineCode ||
        previousFlightNumber !== nextFlightNumber;

      const scheduleFieldsTouched =
        updates.startDate !== undefined ||
        updates.startTime !== undefined ||
        updates.endDate !== undefined ||
        updates.endTime !== undefined;
      const shouldResetDerivedFlightFields = identifiersChanged || scheduleFieldsTouched;

      const effectiveTitle =
        toOptionalText(updates.title !== undefined ? updates.title : currentItemData.title) ||
        'Manual Flight';
      const provider = [nextAirlineCode, nextFlightNumber].filter(Boolean).join(' ') || effectiveTitle;
      const location =
        toOptionalText(updates.location !== undefined ? updates.location : currentItemData.location) ||
        toOptionalText(updates.address !== undefined ? updates.address : currentItemData.address);
      const mergedStartDay =
        toNullableDate(updates.startDate !== undefined ? updates.startDate : currentItemData.startDate) ||
        new Date();
      const mergedStartTime = updates.startTime !== undefined ? updates.startTime : currentItemData.startTime;
      const departureDate = mergeItineraryTimeOntoDay(mergedStartDay, mergedStartTime as string | undefined);
      const mergedEndDay =
        toNullableDate(updates.endDate !== undefined ? updates.endDate : currentItemData.endDate) ??
        mergedStartDay;
      const mergedEndTime = updates.endTime !== undefined ? updates.endTime : currentItemData.endTime;
      const arrivalDate = mergeItineraryTimeOntoDay(mergedEndDay, mergedEndTime as string | undefined);

      const { updateBooking } = await import('./bookings');
      await updateBooking(tripId, linkedBookingId, {
        provider,
        rawEmailSubject: effectiveTitle,
        location,
        departureDate,
        arrivalDate,
        airlineCode: nextAirlineCode,
        flightNumber: nextFlightNumber,
        ...(shouldResetDerivedFlightFields
          ? {
              flightStatus: null,
              lastStatusCheck: null,
              checkInLink: null,
              gate: null,
              terminal: null,
              checkInAvailable: false,
            }
          : {}),
      }, { bypassOffline: options?.bypassOffline });
    }
    
    // Track landmark visit if activity item is marked as completed
    if (updates.status === 'completed') {
      try {
        const currentItemDoc = await getDoc(itemRef);
        if (currentItemDoc.exists()) {
          const itemData = currentItemDoc.data();
          const convertedItem = convertFirestoreItineraryItem(itemId, itemData as FirestoreItineraryItem);
          
          // Only track activity items as landmarks
          if (convertedItem.category === 'activity' && convertedItem.startDate) {
            // Get trip owner to track stats
            const tripRef = doc(db, 'trips', tripId);
            const tripDoc = await getDoc(tripRef);
            if (tripDoc.exists()) {
              const tripData = tripDoc.data();
              const ownerId = tripData.owner;
              
              if (ownerId) {
                // Track landmark visit asynchronously (don't block the update)
                import('./userStats').then(({ trackLandmarkVisit }) => {
                  trackLandmarkVisit(
                    ownerId,
                    itemId,
                    convertedItem.title,
                    convertedItem.location || convertedItem.address || '',
                    convertedItem.startDate
                  ).catch(error => {
                    console.warn('[Itinerary] Failed to track landmark visit:', error);
                  });
                });
              }
            }
          }
        }
      } catch (error) {
        // Don't fail the update if landmark tracking fails
        console.warn('[Itinerary] Error tracking landmark visit:', error);
      }
    }
  } catch (error: any) {
    console.error('Error updating itinerary item:', error);
    if (
      !options?.bypassOffline &&
      (error?.code === 'unavailable' ||
        error?.code === 'failed-precondition' ||
        error?.code === 'deadline-exceeded' ||
        error?.code === 'aborted')
    ) {
      await syncQueue.queueAction({
        type: 'update_itinerary_item',
        payload: {
          tripId,
          itemId,
          updatesJson: JSON.stringify(updates, (_k, v) => (v instanceof Date ? v.toISOString() : v)),
        },
        tripId,
      });
      return;
    }
    throw error;
  }
};

/**
 * Delete an itinerary item
 */
export const deleteItineraryItem = async (
  tripId: string,
  itemId: string,
  options?: { bypassOffline?: boolean },
): Promise<void> => {
  console.log('[Itinerary] deleteItineraryItem called');
  if (!options?.bypassOffline && networkMonitor.shouldUseOfflineDataFallback()) {
    await syncQueue.queueAction({
      type: 'delete_itinerary_item',
      payload: { tripId, itemId },
      tripId,
    });
    void removeItineraryItemFromOfflineCache(tripId, itemId).catch((cacheError) => {
      console.warn('[Itinerary] Failed to prune offline cache after queued delete', {
        tripId,
        itemId,
        cacheError,
      });
    });
    return;
  }
  try {
    const itemRef = doc(db, 'trips', tripId, 'itineraryItems', itemId);
    console.log('[Itinerary] Attempting to delete itinerary item document');
    await deleteDoc(itemRef);
    console.log('[Itinerary] Successfully deleted itinerary item');
    void refreshOfflineItineraryPackFromServer(tripId).catch((refreshError) => {
      console.warn('[Itinerary] Failed to refresh offline pack after delete', {
        tripId,
        itemId,
        refreshError,
      });
    });
  } catch (error: any) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[Itinerary] Error deleting itinerary item', { message });
    if (
      !options?.bypassOffline &&
      (error?.code === 'unavailable' ||
        error?.code === 'failed-precondition' ||
        error?.code === 'deadline-exceeded' ||
        error?.code === 'aborted')
    ) {
      await syncQueue.queueAction({
        type: 'delete_itinerary_item',
        payload: { tripId, itemId },
        tripId,
      });
      void removeItineraryItemFromOfflineCache(tripId, itemId).catch((cacheError) => {
        console.warn('[Itinerary] Failed to prune offline cache after queued delete', {
          tripId,
          itemId,
          cacheError,
        });
      });
      return;
    }
    throw error;
  }
};

/**
 * Group itinerary items by date
 */
export const groupItineraryItemsByDate = (
  items: ItineraryItem[],
  options?: { trip?: Trip },
): Map<string, ItineraryItem[]> => {
  const grouped = new Map<string, ItineraryItem[]>();
  const groupedIdsByDate = new Map<string, Set<string>>();
  const useTripCalendar = Boolean(options?.trip);
  
  items.forEach(item => {
    if (!item || item.startDate == null) {
      console.warn('[Itinerary] Skipping item with missing startDate:', item?.id);
      return;
    }

    const start = coerceDate(item.startDate as unknown);
    if (!start) {
      console.warn('[Itinerary] Skipping item with unparseable startDate:', item.id, item.startDate);
      return;
    }
    
    const dateKeys = new Set<string>();
    try {
      dateKeys.add(
        useTripCalendar ? getItemTripCalendarDateKey(options!.trip!, item) : toLocalDateKey(start),
      );

      if (item.endDate != null) {
        const end = coerceDate(item.endDate as unknown);
        if (end) {
          dateKeys.add(
            useTripCalendar
              ? getItemTripCalendarDateKey(options!.trip!, { startDate: end })
              : toLocalDateKey(end),
          );
        } else {
          console.warn('[Itinerary] Ignoring invalid endDate while grouping:', item.id, item.endDate);
        }
      }
    } catch (error) {
      console.error('[Itinerary] Error processing item date:', item.id, error);
      return;
    }

    dateKeys.forEach(dateKey => {
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      if (!groupedIdsByDate.has(dateKey)) {
        groupedIdsByDate.set(dateKey, new Set<string>());
      }

      const idsForDate = groupedIdsByDate.get(dateKey)!;
      if (idsForDate.has(item.id)) {
        return;
      }

      grouped.get(dateKey)!.push(item);
      idsForDate.add(item.id);
    });
  });
  
  // Sort items within each date by time (earliest to latest)
  // Items with "flexible" time go at the end
  grouped.forEach((itemsForDate) => {
    itemsForDate.sort((a, b) => {
      const aIsFlexible = !a.startTime || a.startTime === 'flexible';
      const bIsFlexible = !b.startTime || b.startTime === 'flexible';
      
      // Both flexible - keep original order
      if (aIsFlexible && bIsFlexible) return 0;
      // Only a is flexible - a goes after b
      if (aIsFlexible) return 1;
      // Only b is flexible - b goes after a
      if (bIsFlexible) return -1;
      // Both have times - sort by time (earliest first)
      const ta = a.startTime ?? '';
      const tb = b.startTime ?? '';
      return ta.localeCompare(tb);
    });
  });
  
  return grouped;
};

/**
 * Check if an itinerary item exists for a booking (to avoid duplicates)
 */
export const getItineraryItemForBooking = async (
  tripId: string,
  bookingId: string
): Promise<ItineraryItem | null> => {
  try {
    const items = await fetchTripItineraryItems(tripId);
    return items.find(item => item.bookingId === bookingId) || null;
  } catch (error) {
    console.error('Error checking for existing itinerary item:', error);
    return null;
  }
};

export type ItineraryGeofenceSource = {
  tripId: string;
  tripDestination?: string | null;
  items: ItineraryItem[];
  radiusMeters?: number;
  dwellMinutes?: number;
};

export type StartItineraryGeofencingOptions = ItineraryGeofenceSource & {
  statusOnCheckIn?: ItineraryItemStatus;
  onEvent?: (event: GeofenceEvent) => void;
  onError?: (error: Error) => void;
};

/**
 * Build point-radius geofences for itinerary items.
 * Uses geocoded address/location to determine coordinates.
 */
export const createItineraryGeofences = async ({
  tripId,
  tripDestination,
  items,
  radiusMeters = 120,
  dwellMinutes = 2,
}: ItineraryGeofenceSource): Promise<Geofence[]> => {
  const fences: Geofence[] = [];

  for (const item of items) {
    const address = item.address || item.location;
    if (!address) {
      continue;
    }

    try {
      const geocodeResult = await geocodeAddress(address, {
        // Use destination city/country for context when available
        country: tripDestination?.split(',')?.[1]?.trim()?.toLowerCase(),
      });

      if (geocodeResult) {
        const [longitude, latitude] = geocodeResult.coordinates;
        fences.push(
          buildPointGeofence({
            id: `itinerary-${item.id}`,
            name: item.title,
            category: 'itinerary',
            coordinates: { latitude, longitude },
            radiusMeters,
            dwellMinutes,
            tripId,
            metadata: {
              itemId: item.id,
              address: item.address,
              location: item.location,
            },
          }),
        );
      }
    } catch (error) {
      // Continue building other geofences even if one fails
      if (__DEV__) {
        console.warn('[Itinerary] Geocoding failed for geofence', item.id, error);
      }
    }
  }

  return fences;
};

/**
 * Start geofencing for itinerary items to auto-check-in on dwell/enter.
 * Returns a cleanup function to stop monitoring.
 */
export const startItineraryCheckInGeofences = async (
  options: StartItineraryGeofencingOptions,
): Promise<() => void> => {
  const {
    tripId,
    tripDestination,
    items,
    radiusMeters = 120,
    dwellMinutes = 2,
    statusOnCheckIn = 'confirmed',
    onEvent,
    onError,
  } = options;

  const fences = await createItineraryGeofences({
    tripId,
    tripDestination,
    items,
    radiusMeters,
    dwellMinutes,
  });

  if (fences.length === 0) {
    return () => {};
  }

  const itemsById = new Map<string, ItineraryItem>();
  items.forEach((item) => itemsById.set(item.id, item));

  const checkedInItems = new Set<string>();

  const stop = startGeofenceMonitoring(fences, {
    onEvent: async (event) => {
      onEvent?.(event);

      const itemId = event.geofence.metadata?.itemId as string | undefined;
      if (!itemId) return;

      // Only mark on dwell to reduce false positives
      if (event.type !== 'dwell' && event.type !== 'enter') return;
      if (checkedInItems.has(itemId)) return;

      checkedInItems.add(itemId);

      try {
        await updateItineraryItem(tripId, itemId, { status: statusOnCheckIn });

        const item = itemsById.get(itemId);
        if (item) {
          await sendItineraryCheckInNotification(item, event.type);
        }
      } catch (error: any) {
        if (__DEV__) {
          console.error('[Itinerary] Failed to auto-check-in itinerary item', itemId, error);
        }
        onError?.(error instanceof Error ? error : new Error(String(error)));
      }
    },
    onError,
  }, {
    // Slightly aggressive sampling for check-ins
    sampleIntervalMs: 10_000,
    distanceIntervalMeters: 20,
    defaultDwellMinutes: dwellMinutes,
  });

  return stop;
};

syncQueue.registerHandler('create_itinerary_item', async (action) => {
  const { tripId, offlineId, item: rawItem } = action.payload as {
    tripId: string;
    offlineId: string;
    item: Record<string, unknown>;
  };

  const parsed = itineraryItemFromQueuePayload(rawItem);
  await runWithFirestoreReconnectGrace(async () => {
    itineraryCreateFromSyncFlush = true;
    try {
      await createItineraryItem(tripId, parsed);
    } finally {
      itineraryCreateFromSyncFlush = false;
    }
  });

  const cached = await getCachedTripData(tripId);
  if (cached?.itinerary && Array.isArray(cached.itinerary)) {
    const next = cached.itinerary.filter((row: any) => row?.id !== offlineId);
    await cacheTripData(
      cached.trip,
      {
        expenses: cached.expenses,
        bookings: cached.bookings,
        itinerary: next,
      },
      { force: true },
    );
  }
});

syncQueue.registerHandler('update_itinerary_item', async (action) => {
  const { tripId, itemId, updatesJson } = action.payload as {
    tripId: string;
    itemId: string;
    updatesJson: string;
  };
  const updates = JSON.parse(updatesJson) as Record<string, unknown>;
  const reviveDateField = (key: string) => {
    const value = updates[key];
    if (typeof value === 'string') {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) updates[key] = d;
    }
  };
  reviveDateField('startDate');
  reviveDateField('endDate');
  if (
    updates.notes &&
    typeof updates.notes === 'object' &&
    !Array.isArray(updates.notes) &&
    typeof (updates.notes as Record<string, unknown>).updatedAt === 'string'
  ) {
    const d = new Date((updates.notes as Record<string, unknown>).updatedAt as string);
    if (!Number.isNaN(d.getTime())) {
      (updates.notes as Record<string, unknown>).updatedAt = d;
    }
  }
  if (
    updates.bookingSnapshot &&
    typeof updates.bookingSnapshot === 'object' &&
    !Array.isArray(updates.bookingSnapshot) &&
    typeof (updates.bookingSnapshot as Record<string, unknown>).startDate === 'string'
  ) {
    const d = new Date((updates.bookingSnapshot as Record<string, unknown>).startDate as string);
    if (!Number.isNaN(d.getTime())) {
      (updates.bookingSnapshot as Record<string, unknown>).startDate = d;
    }
  }
  await runWithFirestoreReconnectGrace(async () => {
    await updateItineraryItem(
      tripId,
      itemId,
      updates as Partial<Omit<ItineraryItem, 'id' | 'tripId' | 'createdBy' | 'createdAt' | 'updatedAt'>>,
      { bypassOffline: true },
    );
  });
});

syncQueue.registerHandler('delete_itinerary_item', async (action) => {
  const { tripId, itemId } = action.payload as {
    tripId: string;
    itemId: string;
  };
  await runWithFirestoreReconnectGrace(async () => {
    await deleteItineraryItem(tripId, itemId, { bypassOffline: true });
  });
});

